import { ResultOf } from "@graphql-typed-document-node/core";
import { uniqBy } from "lodash";
import { useEffect, useState } from "react";
import { CombinedError, useQuery } from "urql";

import { getFragmentData, graphql } from "@/gen";

const useSearchCommentators_Commentator = graphql(`
    fragment useSearchCommentators_Commentator on Commentator {
        id
        displayName
    }
`);

export type CommentatorT = ResultOf<typeof useSearchCommentators_Commentator>;

const COMMENTATORS_PAGE_SIZE = 50;

type UseSearchCommentatorsResult = {
    commentators: CommentatorT[];
    hasMore: boolean;
    commentatorCount: number | null;
    pageCounts: number | null;
    loading: boolean;
    error?: CombinedError | null;
    refetch: () => void;
};

export const useSearchCommentators = ({
    query,
    currentPage,
    pause = false,
}: {
    query: string;
    currentPage: number;
    pause?: boolean;
}): UseSearchCommentatorsResult => {
    const [commentators, setCommentators] = useState<CommentatorT[]>([]);

    const offset = currentPage * COMMENTATORS_PAGE_SIZE;

    const [result, refetch] = useQuery({
        query: graphql(`
            query searchCommentators($input: SearchCommentatorsInput!) {
                searchCommentators(input: $input) {
                    items {
                        id
                        displayName
                        ...useSearchCommentators_Commentator
                    }
                    total
                    hasMore
                }
            }
        `),
        variables: {
            input: {
                query,
                limit: 50,
                offset,
            },
        },
        pause,
    });

    useEffect(() => {
        if (result.data?.searchCommentators?.items) {
            const commentators = getFragmentData(
                useSearchCommentators_Commentator,
                result.data.searchCommentators.items
            );
            setCommentators(uniqBy(commentators ?? [], "id"));
        }
    }, [result.data?.searchCommentators?.items]);

    return {
        commentators,
        hasMore: result.data?.searchCommentators?.hasMore ?? false,
        commentatorCount: result.data?.searchCommentators?.total ?? null,
        pageCounts: result.data?.searchCommentators?.total
            ? Math.ceil(result.data.searchCommentators.total / 50)
            : null,
        loading: result.fetching,
        error: result.error,
        refetch,
    };
};
